import { KeyboardEvent, useEffect, useRef, useState } from 'react'

import { Paper, Popper, Stack, Typography } from '@mui/material'
import { GeoBlockingTemplateCountryAutocomplete } from '@/components/forms/GeoBlockingTemplateForm/GeoBlockingTemplateCountryAutocomplete'
import {
  GeoRegion,
  RegionFilterCode,
  regions,
  territories
} from '@/components/forms/GeoBlockingTemplateForm/GeoBlockingTemplateForm.constants'
import { SelectedRegionsChips } from '@/components/forms/GeoBlockingTemplateForm/SelectedRegionsChips'
import { SelectBox } from '@/components/inputs/SelectBox'

type StreamingCountrySelectPopperProps = {
  anchorEl: HTMLElement | null
  open: boolean
  onClose: () => void
  value: string[]
  onChange: (regionCodes: string[]) => void
  whitelist?: boolean
}

export const StreamingCountrySelectPopper = ({
  anchorEl,
  open,
  onClose,
  value,
  onChange,
  whitelist
}: StreamingCountrySelectPopperProps) => {
  const popperRef = useRef<HTMLDivElement>(null)
  const [regionFilter, setRegionFilter] = useState<RegionFilterCode>('All')
  const [selectBoxOpen, setSelectBoxOpen] = useState(false)

  const selectedRegions = value
    .map((code) => territories.find((territory) => territory.iso2 === code))
    .filter((region): region is GeoRegion => region !== undefined)

  const handleRegionFilterChange = (filter: RegionFilterCode) => {
    setRegionFilter(filter)
  }

  const handleSelectBoxOpen = () => {
    setSelectBoxOpen(true)
  }

  const handleSelectBoxClose = () => {
    setSelectBoxOpen(false)
  }

  const handleRegionsChange = (regions: GeoRegion[]) => {
    const regionCodes = regions.map((region) => region.iso2)
    onChange(regionCodes)
  }

  const handleRemoveRegion = (regionToRemove: GeoRegion) => {
    const updatedCodes = selectedRegions
      .filter((region) => region.iso2 !== regionToRemove.iso2)
      .map((region) => region.iso2)
    onChange(updatedCodes)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }

  const filteredTerritories = territories.filter((territory) => {
    if (regionFilter === 'All') return true

    const selectedContinent = regions.find((c) => c.name === regionFilter)

    if (!selectedContinent) return false

    if (selectedContinent.name === 'US State' || selectedContinent.name === 'CA Province') {
      return territory.continent === selectedContinent.name
    } else {
      return territory.continent === selectedContinent.code
    }
  })

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (popperRef.current && popperRef.current.contains(target)) return
      if (anchorEl && anchorEl.contains(target)) return
      if (selectBoxOpen) return

      onClose()
    }

    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside)
    }, 100)

    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, onClose, anchorEl, selectBoxOpen])

  if (!open) return null

  return (
    <Popper
      ref={popperRef}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      disablePortal={false}
      sx={{
        zIndex: (theme) => theme.zIndex.modal,
        width: 600
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        },
        {
          name: 'flip',
          enabled: true,
          options: {
            fallbackPlacements: ['top-start'],
            rootBoundary: 'viewport',
            padding: 16
          }
        },
        {
          name: 'preventOverflow',
          enabled: true,
          options: {
            altAxis: false,
            mainAxis: false,
            rootBoundary: 'viewport',
            padding: 8
          }
        }
      ]}
    >
      <Paper
        elevation={8}
        onKeyDown={handleKeyDown}
        sx={{
          backgroundColor: 'background.default',
          borderRadius: 1
        }}
      >
        <Stack direction="row">
          <Stack
            sx={{
              width: '52.5%',
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
              backgroundColor: 'background.paper'
            }}
          >
            <SelectBox
              options={regions.map((region) => region.name)}
              value={regionFilter}
              onChange={handleRegionFilterChange}
              label="Region"
              onOpen={handleSelectBoxOpen}
              onClose={handleSelectBoxClose}
            />

            <GeoBlockingTemplateCountryAutocomplete
              options={filteredTerritories}
              value={selectedRegions}
              onChange={handleRegionsChange}
              placeholder="Search for territories..."
              maxHeight={290}
            />
          </Stack>

          <Stack
            sx={{
              width: '47.5%',
              py: 1,
              pl: 1.25,
              pr: 0.25
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={15}
              pt={0.25}
              pb={1}
              sx={{
                color: ({ palette }) =>
                  palette.mode === 'light' ? palette.secondary.dark : 'primary'
              }}
            >
              {whitelist ? 'Whitelisted' : 'Blacklisted'}
            </Typography>
            <SelectedRegionsChips
              selectedRegions={selectedRegions}
              regionFilter={regionFilter}
              onRemoveRegion={handleRemoveRegion}
              height={353}
            />
          </Stack>
        </Stack>
      </Paper>
    </Popper>
  )
}
